import { useCallback, useState, type FocusEvent, type ReactNode } from 'react';
import { useSession } from '../../lib/session.tsx';
import { useSettings } from '../../lib/settings.ts';
import { useMediaQuery } from '../../lib/use-media-query.ts';
import { FeedbackWidget } from './feedback-widget.tsx';
import { OrgPicker } from './org-picker.tsx';
import { PendingActivation } from './pending-activation.tsx';
import { Sidebar } from './sidebar.tsx';
import { SidebarHoldContext } from './sidebar-hold.ts';
import { MobileTopbar } from './topbar.tsx';

/** Below this width the sidebar is a drawer: a 256px sidebar would leave a phone ~100px of page. */
const NARROW = '(max-width: 767px)';

/**
 * The CRM's layout, as Leve's (TS-1681): one canvas, the sidebar floating on it as a card
 * (organisation, navigation, theme, profile all inside it), and the page beside it. On desktop the
 * sidebar is always the icon rail and expands over the page — an overlay, nothing is pushed —
 * while the pointer or keyboard focus is in it (`DesktopSidebar`). The shell fills the viewport
 * and only `main` scrolls, so a long page never scrolls the sidebar with it and a wide table
 * scrolls inside the page.
 */
export function Shell({ children }: { children: ReactNode }) {
  const session = useSession();
  const [settings, update] = useSettings(session.me.user.id);
  const narrow = useMediaQuery(NARROW);
  // An invited account is a Viewer the API refuses everything but /me: show that, not a
  // sidebar of pages that would each fail with "Account not yet activated" (TS-956).
  if (session.me.user.status === 'invited') return <PendingActivation />;
  const groups = {
    collapsedGroups: settings.collapsedNavGroups,
    onToggleGroup: (group: string) =>
      update({
        collapsedNavGroups: settings.collapsedNavGroups.includes(group)
          ? settings.collapsedNavGroups.filter((g) => g !== group)
          : [...settings.collapsedNavGroups, group],
      }),
  };
  return (
    <div className="flex h-dvh w-full overflow-hidden">
      {!narrow && <DesktopSidebar {...groups} />}
      <div className="flex min-w-0 flex-1 flex-col">
        {narrow && <MobileTopbar sidebar={<Sidebar collapsed={false} {...groups} />} />}
        {/* `relative` makes the scroll area the box absolutely positioned descendants sit in,
            so its overflow clips them. Without it Radix Select's hidden native <select> (one
            beside every Select in a form, `position: absolute`) was placed against the page,
            stretched the document past this h-dvh frame, and scrolling past the end of the
            page showed a white band of bare body under it. */}
        <main className="relative min-h-0 min-w-0 flex-1 overflow-auto">
          <div className="w-full px-3 pt-2 pb-12 sm:px-6 md:pt-3 lg:px-10">{children}</div>
        </main>
      </div>
      <OrgPicker />
      <FeedbackWidget />
    </div>
  );
}

/** The rail's slot (68px + padding) stays fixed; the sidebar grows out of it over the page. */
function DesktopSidebar(props: {
  collapsedGroups: readonly string[];
  onToggleGroup: (group: string) => void;
}) {
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [held, setHeld] = useState(false);
  const hold = useCallback((open: boolean) => setHeld(open), []);
  const expanded = hover || focus || held;
  return (
    <div className="relative h-full w-[92px] shrink-0">
      <div
        className="absolute inset-y-0 left-0 z-40 p-3"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={(e: FocusEvent) => setFocus(e.target.matches(':focus-visible'))}
        onBlur={(e: FocusEvent) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setFocus(false);
        }}
        data-testid="sidebar-slot"
      >
        <SidebarHoldContext.Provider value={hold}>
          <Sidebar collapsed={!expanded} {...props} />
        </SidebarHoldContext.Provider>
      </div>
    </div>
  );
}

export function ShellSkeleton() {
  return (
    <div className="flex h-dvh w-full" aria-busy="true" aria-label="Loading">
      <div className="hidden p-3 md:block">
        <div className="bg-card border-border rounded-sheet shadow-card h-full w-64 border" />
      </div>
      <div className="flex flex-1 flex-col px-3 pt-16 sm:px-6 md:pt-3 lg:px-10">
        <div className="bg-muted h-8 w-56 animate-pulse rounded-lg" />
        <div className="bg-muted mt-3 h-4 w-80 max-w-full animate-pulse rounded" />
      </div>
    </div>
  );
}
